import { defineAsyncComponent } from 'vue';

/**
 * The raw chunk loaders, keyed exactly as the registry below.
 *
 * Exported so a view can PRELOAD the sections a page is about to render rather
 * than discovering them one chunk at a time. Every section here is its own lazy
 * chunk, so a layout that has "rendered" has in fact only rendered its Header,
 * a placeholder per section, and its Footer — which is why lifting the loading
 * overlay at that point showed a bare footer for a second while the sections
 * streamed in behind it. TemplateRenderer awaits these before revealing.
 *
 * Kept as plain functions: webpack caches the module promise, so calling one a
 * second time is free and the matching defineAsyncComponent resolves without a
 * further round trip.
 */
export const sectionLoaders = {
    AboutProjectSection: () => import('./components/sections/AboutProjectSection.vue'),
    CoreElementsSection: () => import('./components/sections/CoreElementsSection.vue'),
    FAQSection: () => import('./components/sections/FAQSection.vue'),
    FitnessManagementPresentationSection: () => import('./components/sections/FitnessManagementPresentationSection.vue'),
    FlowChart: () => import('./components/sections/FlowChart.vue'),
    PlatformCapabilitySection: () => import('./components/sections/PlatformCapabilitySection.vue'),
    AutomotiveHeroSection: () => import('./components/sections/AutomotiveHeroSection.vue'),
    FitnessHeroSection: () => import('./components/sections/FitnessHeroSection.vue'),
    SystemAnatomySection: () => import('./components/sections/SystemAnatomySection.vue'),
    HeroSection: () => import('./components/sections/HeroSection.vue'),
    HowProjectWorksSection: () => import('./components/sections/HowProjectWorksSection.vue'),
    IncidentManagementHeroSection: () => import('./components/sections/IncidentManagementHeroSection.vue'),
    IncidentManagementKeyFeaturesSection: () => import('./components/sections/IncidentManagementKeyFeaturesSection.vue'),
    IncidentManagementSolutionsSection: () => import('./components/sections/IncidentManagementSolutionsSection.vue'),
    KeyFeaturesSection: () => import('./components/sections/KeyFeaturesSection.vue'),
    LoyaltyFlowChart: () => import('./components/sections/LoyaltyFlowChart.vue'),
    LoyaltyManagementEssentialAppsSection: () => import('./components/sections/LoyaltyManagementEssentialAppsSection.vue'),
    LoyaltyManagementHeroSection: () => import('./components/sections/LoyaltyManagementHeroSection.vue'),
    LoyaltyManagementKeyFeaturesSection: () => import('./components/sections/LoyaltyManagementKeyFeaturesSection.vue'),
    LoyaltyManagementPowerfulFeaturesSection: () => import('./components/sections/LoyaltyManagementPowerfulFeaturesSection.vue'),
    PowerfulFeatureSection: () => import('./components/sections/PowerfulFeatureSection.vue'),
    PresentationSection: () => import('./components/sections/PresentationSection.vue'),
    ProcurementFlowChart: () => import('./components/sections/ProcurementFlowChart.vue'),
    ProjectProsSection: () => import('./components/sections/ProjectProsSection.vue'),
    PropertyManagementAboutProjectSection: () => import('./components/sections/PropertyManagementAboutProjectSection.vue'),
    PropertyManagementCoreElementsSection: () => import('./components/sections/PropertyManagementCoreElementsSection.vue'),
    PropertyManagementFeaturesSection: () => import('./components/sections/PropertyManagementFeaturesSection.vue'),
    PropertyManagementHeroSection: () => import('./components/sections/PropertyManagementHeroSection.vue'),
    PropertyManagementProsSection: () => import('./components/sections/PropertyManagementProsSection.vue'),
    PropertyManagementSolutionSection: () => import('./components/sections/PropertyManagementSolutionSection.vue'),
    ServiceProcurementHeroSection: () => import('./components/sections/ServiceProcurementHeroSection.vue'),
    ServiceProcurementKeyFeatures: () => import('./components/sections/ServiceProcurementKeyFeatures.vue'),
    ZohoProductsSection: () => import('./components/sections/ZohoProductsSection.vue')
};

// Built from the same loaders, so the two can never drift apart. A stray static
// `import FitnessHeroSection from ...` used to sit at the top of this file
// alongside the async entry of the same name, pulling that component into the
// main bundle for nothing.
export default Object.fromEntries(
    Object.entries(sectionLoaders).map(([name, load]) => [name, defineAsyncComponent(load)])
);
